import { VIEW_TYPES } from "@/other/constants/OtherKeys";
import type { Subcategory } from "@/react/components/entityViews/settings/constants/settingsStructure";
import type {
  EntityFinderConfigEntry,
  EntityFinderConfigEntryRuntime,
  EntityFinderContext,
  EntityFinderItem,
  EntityFinderKind,
  EntityFinderListEntry,
  GroupedFinderResults,
} from "@/modern/components/entity-finder/types";

// --- search ---

const STOP_WORDS = new Set(["a", "an", "the", "to", "for", "of"]);

export function matchesSearch(value: unknown, searchLower: string): boolean {
  if (!searchLower || value == null) return false;
  return String(value).toLowerCase().includes(searchLower);
}

export function passesArchiveFilter(archived: boolean | undefined, isShowArchive: boolean): boolean {
  return isShowArchive || !archived;
}

export function matchesEntityFinderSearchQuery(searchableText: string, query: string): boolean {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return false;

  const textLower = searchableText.toLowerCase();
  if (textLower.includes(trimmed)) return true;

  const words = trimmed.split(/\s+/).filter((word) => word && !STOP_WORDS.has(word));
  if (words.length === 0) return textLower.includes(trimmed);

  return words.every((word) => textLower.includes(word));
}

export function defaultItemSearch<T extends { label: string; subtitle?: string }>(
  query: string,
  items: T[]
): T[] {
  const trimmed = query.trim();
  if (!trimmed) return items;

  return items.filter((item) =>
    matchesEntityFinderSearchQuery([item.label, item.subtitle].filter(Boolean).join(" "), trimmed)
  );
}

// --- navigation ---

export function navigateToSetting(
  viewManager: EntityFinderContext["viewManager"],
  subcategory: Subcategory
) {
  if (!viewManager) return;
  viewManager.openAnonymousViewByType({
    viewType: VIEW_TYPES.settings,
    viewName: "Settings",
    otherParams: {
      setting: subcategory.settingId ?? subcategory.id,
      settingSubcategoryId: subcategory.id,
    },
  });
}

// --- list ---

export function defaultMatchesScope(ctx: EntityFinderContext, type: EntityFinderKind): boolean {
  return ctx.scopes.length === 0 || ctx.scopes.includes(type);
}

export function searchModeActive(_ctx: EntityFinderContext, mode: "idle" | "search"): boolean {
  return mode === "search";
}

export function defaultEntityListSection<K extends EntityFinderKind>(
  entry: EntityFinderConfigEntry<K>,
  items: EntityFinderItem<K>[]
): EntityFinderListEntry[] {
  if (items.length === 0) return [];

  const entries: EntityFinderListEntry[] = [
    { type: "header", label: entry.plural, configType: entry.type },
  ];

  for (const item of items) {
    entries.push({ type: "item", item, configType: entry.type });
  }

  return entries;
}

export function buildEntityFinderListEntries(
  grouped: GroupedFinderResults,
  _ctx: EntityFinderContext
): EntityFinderListEntry[] {
  return grouped
    .slice()
    .sort((a, b) => a.entry.listOrder - b.entry.listOrder)
    .flatMap(({ entry, items }) => entry.buildListSection(items, _ctx));
}

// --- recents ---

function getEntityFinderItemUpdatedAt(item: EntityFinderItem): number {
  const record = item.record as {
    updatedAt?: string | Date | number;
    createdAt?: string | Date | number;
    lastInteractionHQ?: string | Date | number;
    lastLogin?: string | Date | number;
  };
  const value =
    record?.updatedAt ??
    record?.lastInteractionHQ ??
    record?.lastLogin ??
    record?.createdAt;
  return value != null ? new Date(value).getTime() : 0;
}

export function collectRecents(
  ctx: EntityFinderContext,
  max: number,
  config: readonly EntityFinderConfigEntryRuntime[]
): EntityFinderItem[] {
  const scoped = config.filter((entry) => entry.matchesScope(ctx));
  const items: EntityFinderItem[] = [];
  const seen = new Set<string>();

  const viewsEntry = scoped.find((entry) => entry.type === "views");
  const pinnedView = viewsEntry?.getRecentItem?.(ctx);
  if (pinnedView) {
    items.push(pinnedView);
    seen.add(pinnedView.id);
  }

  const entityItems: EntityFinderItem[] = [];
  for (const entry of scoped) {
    if (entry.type === "views") continue;
    if (entry.getRecentItems) {
      entityItems.push(...entry.getRecentItems(ctx));
    } else {
      const recent = entry.getRecentItem?.(ctx);
      if (recent) entityItems.push(recent);
    }
  }

  const sortedEntities = entityItems
    .filter((item) => !seen.has(item.id))
    .sort((a, b) => getEntityFinderItemUpdatedAt(b) - getEntityFinderItemUpdatedAt(a));

  items.push(...sortedEntities);
  return items.slice(0, max);
}
